export { DealTaskDetails } from "./DealTaskDetails";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useHistory, useParams } from "react-router-dom";
import { format, formatDistanceToNowStrict, fromUnixTime, isBefore } from "date-fns";
import {
    CalendarIcon,
    CheckCircle2Icon,
    ChevronDownIcon,
    CircleDashedIcon,
    CircleIcon,
    KanbanIcon,
    ListIcon,
    PlusIcon,
    SignalIcon,
    UserPlusIcon,
    ListTodoIcon,
} from "lucide-react";
import {
    DragDropContext,
    Draggable,
    Droppable,
    DropResult,
} from "@hello-pangea/dnd";
import { useQueryClient } from "@tanstack/react-query";

import { Button, ButtonPopout } from "../../../components/tailwind/Button";
import { Avatar } from "../../../components/account/Avatar";
import { TaskPriorityIcon } from "../../../components/TaskPriorityPill";
import { FilterButton, FilterOption } from "../../../components/data_room/filters/filter_button";
import { Toggle } from "../../../components/Toggle";
import { NewDealTaskModal } from "./NewDealTaskModal";
import { classNames } from "../../../utils/cn";
import useGqlClient from "../../../hooks/useGqlClient";
import { authSelectors } from "../../../store/auth/selector";
import { Checkbox } from "../../../components/checkbox";
import { CloseIcon } from "../../../components/CloseIcon";
import { useShiftSelection } from "../../../hooks/useShiftSelection";
import {
    Account,
    Task,
    TaskPriority,
    TaskStatus,
    useDealTasksQuery, useUpdateTaskMutation,
    useUpdateTasksMutation
} from "../../../graphql/generated";
import { toasts } from "../../../components/toasts/toasts";

type TaskFilters = {
    assignee?: Pick<Account, "id" | "name" | "avatar">;
    priority?: TaskPriority;
};

type ViewMode = "list" | "kanban";

// Context for task selection state
type TasksContextType = {
    tasks: Task[];
    selectedTasks: string[];
    setSelectedTasks: (ids: string[]) => void;
    assigneeOptions: { id: string; name: string; avatar?: string | null }[];
};

export const TasksContext = createContext<TasksContextType>({
    tasks: [],
    selectedTasks: [],
    setSelectedTasks: () => { },
    assigneeOptions: [],
});

export function DealTasks() {
    const { dealId } = useParams<{ dealId: string }>();
    const account = useSelector(authSelectors.account);
    const client = useGqlClient();
    const queryClient = useQueryClient();

    const [showNewTaskModal, setShowNewTaskModal] = useState(false);
    const [filters, setFilters] = useState<TaskFilters>({});
    const [viewMode, setViewMode] = useState<ViewMode>("list");
    const [localTasks, setLocalTasks] = useState<Task[]>([]);
    const [selectedTasks, setSelectedTasks] = useState<string[]>([]);

    const updateTask = useUpdateTaskMutation(client);

    const tasksQuery = useDealTasksQuery(client, {
        filters: {
            dealID: dealId,
        },
    });

    // Sync local tasks with server data
    useEffect(() => {
        if (tasksQuery.data?.tasks) {
            setLocalTasks(tasksQuery.data.tasks as Task[]);
        }
    }, [tasksQuery.data?.tasks]);

    // Build filter options from task data
    const assigneeOptions = useMemo<FilterOption<Pick<Account, "id" | "name" | "avatar">>[]>(() => {
        const assignees = new Map<string, Pick<Account, "id" | "name" | "avatar">>();
        localTasks.forEach((task) => {
            task.assignedTo?.forEach((a) => {
                if (!assignees.has(a.id)) {
                    assignees.set(a.id, { id: a.id, name: a.name, avatar: a.avatar });
                }
            });
        });
        return Array.from(assignees.values()).map((a) => ({
            label: a.name,
            value: a,
        }));
    }, [localTasks]);

    // Build unique assignees list for context (used in bulk actions)
    const uniqueAssignees = useMemo(() => {
        const assignees = new Map<string, { id: string; name: string; avatar?: string | null }>();
        localTasks.forEach((task) => {
            task.assignedTo?.forEach((a) => {
                if (!assignees.has(a.id)) {
                    assignees.set(a.id, { id: a.id, name: a.name, avatar: a.avatar });
                }
            });
        });
        return Array.from(assignees.values());
    }, [localTasks]);

    const priorityOptions: FilterOption<TaskPriority>[] = [
        { label: "Urgent", value: TaskPriority.Urgent },
        { label: "High", value: TaskPriority.High },
        { label: "Medium", value: TaskPriority.Medium },
        { label: "Low", value: TaskPriority.Low },
    ];

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        if (result.source.droppableId === result.destination.droppableId) return;

        const taskId = result.draggableId;
        const newStatus = result.destination.droppableId as TaskStatus;

        // Optimistic update - immediately update local state
        setLocalTasks((prev) =>
            prev.map((task) =>
                task.id === taskId ? { ...task, status: newStatus } : task
            )
        );

        // Then mutate on the server
        updateTask.mutate(
            { input: { id: taskId, status: newStatus } },
            {
                onError: () => {
                    // Revert on error by refetching
                    queryClient.invalidateQueries({ queryKey: ["Tasks"] });
                    toasts.error("Failed to update task status");
                },
            }
        );
    };

    if (tasksQuery.isLoading || !tasksQuery.data) {
        return (
            <div className="p-4 w-full">
                <div className="animate-pulse">
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex gap-x-2">
                            <div className="h-8 bg-gray-200 rounded w-28"></div>
                            <div className="h-8 bg-gray-200 rounded w-24"></div>
                        </div>
                        <div className="h-9 bg-gray-200 rounded w-28"></div>
                    </div>
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i}>
                                <div className="h-8 bg-gray-100 rounded w-32 mb-2"></div>
                                {[...Array(2)].map((_, j) => (
                                    <div key={j} className="flex gap-x-4 py-2">
                                        <div className="h-5 bg-gray-200 rounded w-5"></div>
                                        <div className="h-5 bg-gray-200 rounded flex-1"></div>
                                        <div className="h-5 bg-gray-200 rounded w-24"></div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (tasksQuery.error) {
        return (
            <div className="p-8 flex justify-center items-center">
                <p className="text-sm font-semibold text-gray-500">Something went wrong</p>
            </div>
        );
    }

    // Apply filters
    const allTasks = localTasks;
    const tasks = allTasks.filter((task) => {
        if (filters.assignee && !task.assignedTo?.some((a) => a.id === filters.assignee?.id)) {
            return false;
        }
        if (filters.priority && task.priority !== filters.priority) {
            return false;
        }
        return true;
    });

    // Group tasks by status
    const todoTasks = tasks.filter((t) => t.status === TaskStatus.Todo);
    const inProgressTasks = tasks.filter((t) => t.status === TaskStatus.InProgress);
    const completedTasks = tasks.filter((t) => t.status === TaskStatus.Completed);

    const hasNoTasks = allTasks.length === 0;
    const hasNoFilteredTasks = tasks.length === 0 && !hasNoTasks;

    return (
        <TasksContext.Provider
            value={{
                tasks: localTasks,
                selectedTasks,
                setSelectedTasks,
                assigneeOptions: uniqueAssignees,
            }}
        >
            <div className={classNames(
                "overflow-hidden",
                viewMode === "kanban" ? "h-[calc(100vh-200px)]" : "max-h-[calc(100vh-200px)] overflow-y-auto"
            )}>
                {/* Header */}
                <div className="flex items-center justify-between p-2 pt-0">
                    <div className="flex items-center gap-x-2">
                        <FilterButton
                            defaultLabel="Assignee"
                            options={assigneeOptions}
                            selected={assigneeOptions.find((o) => o.value.id === filters.assignee?.id)}
                            onChange={(value) => {
                                setFilters({ ...filters, assignee: value });
                            }}
                            renderOption={(option) => (
                                <div className="flex items-center gap-x-2">
                                    <Avatar account={option.value} size="xs" />
                                    <p className="text-sm font-semibold text-gray-500">{option.label}</p>
                                </div>
                            )}
                        />
                        <FilterButton
                            defaultLabel="Priority"
                            options={priorityOptions}
                            selected={priorityOptions.find((o) => o.value === filters.priority)}
                            onChange={(value) => {
                                setFilters({ ...filters, priority: value });
                            }}
                            renderOption={(option) => (
                                <div className="flex items-center gap-x-2">
                                    <TaskPriorityIcon priority={option.value} />
                                    <p className="text-sm font-semibold text-gray-500">{option.label}</p>
                                </div>
                            )}
                        />
                        <Toggle
                            options={[
                                { id: "list", icon: ListIcon },
                                { id: "kanban", icon: KanbanIcon },
                            ]}
                            value={viewMode}
                            onChange={(v) => {
                                setViewMode(v as ViewMode);
                                // Clear selection when switching to kanban
                                if (v === "kanban") {
                                    setSelectedTasks([]);
                                }
                            }}
                            variant="secondary"
                            size="sm"
                        />
                    </div>
                    <TaskSelectionActions
                        onNewTask={() => setShowNewTaskModal(true)}
                    />
                </div>

                {/* Content */}
                {hasNoTasks ? (
                    <EmptyState onNewTask={() => setShowNewTaskModal(true)} />
                ) : hasNoFilteredTasks ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4">
                        <p className="text-gray-500 text-sm">No tasks match the current filters</p>
                    </div>
                ) : viewMode === "list" ? (
                    <div className="divide-y divide-gray-100 overflow-y-auto max-h-[calc(100vh-260px)]">
                        <TaskSection
                            title="Todo"
                            tasks={todoTasks}
                            icon={<CircleDashedIcon className="w-4 h-4 text-gray-400" />}
                            defaultOpen={true}
                            dealId={dealId ?? ""}
                        />
                        <TaskSection
                            title="In Progress"
                            tasks={inProgressTasks}
                            icon={<CircleIcon className="w-4 h-4 text-blue-500" />}
                            defaultOpen={true}
                            dealId={dealId ?? ""}
                        />
                        <TaskSection
                            title="Done"
                            tasks={completedTasks}
                            icon={<CheckCircle2Icon className="w-4 h-4 text-green-500" />}
                            defaultOpen={false}
                            dealId={dealId ?? ""}
                        />
                    </div>
                ) : (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <KanbanView
                            todoTasks={todoTasks}
                            inProgressTasks={inProgressTasks}
                            completedTasks={completedTasks}
                            dealId={dealId ?? ""}
                        />
                    </DragDropContext>
                )}

                {/* New Task Modal */}
                {account?.firm && dealId && (
                    <NewDealTaskModal
                        open={showNewTaskModal}
                        onClose={() => setShowNewTaskModal(false)}
                        firmID={account.firm.id}
                        dealID={dealId}
                    />
                )}
            </div>
        </TasksContext.Provider>
    );
}

function EmptyState({ onNewTask }: { onNewTask: () => void }) {
    return (
        <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <CheckCircle2Icon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No tasks yet</h3>
            <p className="text-gray-500 text-sm text-center mb-6 max-w-sm">
                Create tasks to track work items for this deal
            </p>
            <Button
                icon={PlusIcon}
                text="Create your first task"
                variant="positive"
                size="s"
                onClick={onNewTask}
            />
        </div>
    );
}

// =============================================================================
// Task Selection Actions
// =============================================================================

function TaskSelectionActions({ onNewTask }: { onNewTask: () => void }) {
    const { selectedTasks, setSelectedTasks, tasks, assigneeOptions } = useContext(TasksContext);
    const [openModal, setOpenModal] = useState<"" | "status" | "priority" | "assignee">("");
    const client = useGqlClient();
    const queryClient = useQueryClient();
    const updateTasks = useUpdateTasksMutation(client);

    const handleUpdateStatus = (status: TaskStatus) => {
        toasts.info("Updating tasks...");
        updateTasks.mutate(
            {
                input: {
                    taskInputs: selectedTasks.map((id) => ({
                        id,
                        status,
                    })),
                },
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ["Tasks"] });
                    setSelectedTasks([]);
                    setOpenModal("");
                    toasts.success("Tasks updated");
                },
                onError: () => {
                    toasts.error("Failed to update tasks");
                },
            }
        );
    };

    const handleUpdatePriority = (priority: TaskPriority) => {
        toasts.info("Updating tasks...");
        updateTasks.mutate(
            {
                input: {
                    taskInputs: selectedTasks.map((id) => ({
                        id,
                        priority,
                    })),
                },
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ["Tasks"] });
                    setSelectedTasks([]);
                    setOpenModal("");
                    toasts.success("Tasks updated");
                },
                onError: () => {
                    toasts.error("Failed to update tasks");
                },
            }
        );
    };

    const handleUpdateAssignee = (assigneeId: string) => {
        toasts.info("Updating tasks...");
        // Get current assignees for each task and add the new one
        const taskInputs = selectedTasks.map((id) => {
            const task = tasks.find((t) => t.id === id);
            const currentAssignees = task?.assignedTo?.map((a) => a.id) ?? [];
            const newAssignees = currentAssignees.includes(assigneeId)
                ? currentAssignees
                : [...currentAssignees, assigneeId];
            return {
                id,
                assignedTo: newAssignees,
            };
        });

        updateTasks.mutate(
            {
                input: {
                    taskInputs,
                },
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ["Tasks"] });
                    setSelectedTasks([]);
                    setOpenModal("");
                    toasts.success("Tasks updated");
                },
                onError: () => {
                    toasts.error("Failed to update tasks");
                },
            }
        );
    };

    if (selectedTasks.length === 0) {
        return (
            <Button
                icon={PlusIcon}
                text="New task"
                variant="positive"
                size="s"
                onClick={onNewTask}
            />
        );
    }

    return (
        <div className="flex items-center bg-concrete-100 box-content rounded-md px-2 py-1 gap-x-2">
            <div className="flex items-center space-x-2">
                <CloseIcon onClose={() => setSelectedTasks([])} />
                <span className="text-sm font-semibold text-gray-600">
                    {selectedTasks.length} selected
                </span>
            </div>

            {/* Set Status */}
            <Button
                id="set-task-status"
                size="s"
                variant="secondary"
                icon={ListTodoIcon}
                text="Set status"
                onClick={() => {
                    setOpenModal(openModal === "status" ? "" : "status");
                }}
            />
            <ButtonPopout
                buttonElementId="set-task-status"
                open={openModal === "status"}
                onClose={() => setOpenModal("")}
                className="w-48"
            >
                <div>
                    <p className="p-2 text-persian-990 font-semibold text-xs">
                        Set status
                    </p>
                    <StatusOption
                        status={TaskStatus.Todo}
                        label="Todo"
                        icon={<CircleDashedIcon className="w-4 h-4 text-gray-400" />}
                        onClick={() => handleUpdateStatus(TaskStatus.Todo)}
                    />
                    <StatusOption
                        status={TaskStatus.InProgress}
                        label="In Progress"
                        icon={<CircleIcon className="w-4 h-4 text-blue-500" />}
                        onClick={() => handleUpdateStatus(TaskStatus.InProgress)}
                    />
                    <StatusOption
                        status={TaskStatus.Completed}
                        label="Done"
                        icon={<CheckCircle2Icon className="w-4 h-4 text-green-500" />}
                        onClick={() => handleUpdateStatus(TaskStatus.Completed)}
                    />
                </div>
            </ButtonPopout>

            {/* Set Priority */}
            <Button
                id="set-task-priority"
                size="s"
                variant="secondary"
                icon={SignalIcon}
                text="Set priority"
                onClick={() => {
                    setOpenModal(openModal === "priority" ? "" : "priority");
                }}
            />
            <ButtonPopout
                buttonElementId="set-task-priority"
                open={openModal === "priority"}
                onClose={() => setOpenModal("")}
                className="w-48"
            >
                <div>
                    <p className="p-2 text-persian-990 font-semibold text-xs">
                        Set priority
                    </p>
                    <PriorityOption
                        priority={TaskPriority.Urgent}
                        onClick={() => handleUpdatePriority(TaskPriority.Urgent)}
                    />
                    <PriorityOption
                        priority={TaskPriority.High}
                        onClick={() => handleUpdatePriority(TaskPriority.High)}
                    />
                    <PriorityOption
                        priority={TaskPriority.Medium}
                        onClick={() => handleUpdatePriority(TaskPriority.Medium)}
                    />
                    <PriorityOption
                        priority={TaskPriority.Low}
                        onClick={() => handleUpdatePriority(TaskPriority.Low)}
                    />
                </div>
            </ButtonPopout>

            {/* Assign To */}
            {assigneeOptions.length > 0 && (
                <>
                    <Button
                        id="assign-task-to"
                        size="s"
                        variant="secondary"
                        icon={UserPlusIcon}
                        text="Assign to"
                        onClick={() => {
                            setOpenModal(openModal === "assignee" ? "" : "assignee");
                        }}
                    />
                    <ButtonPopout
                        buttonElementId="assign-task-to"
                        open={openModal === "assignee"}
                        onClose={() => setOpenModal("")}
                        className="w-56"
                    >
                        <div>
                            <p className="p-2 text-persian-990 font-semibold text-xs">
                                Assign to
                            </p>
                            <div className="max-h-60 overflow-y-auto">
                                {assigneeOptions.map((assignee) => (
                                    <div
                                        key={assignee.id}
                                        className="flex items-center gap-x-2 p-2 hover:bg-persian-50 cursor-pointer"
                                        onClick={() => handleUpdateAssignee(assignee.id)}
                                    >
                                        <Avatar account={{ ...assignee, avatar: assignee.avatar ?? undefined }} size="xs" />
                                        <p className="text-sm text-persian-990">{assignee.name}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </ButtonPopout>
                </>
            )}
        </div>
    );
}

function StatusOption({
    status,
    label,
    icon,
    onClick,
}: {
    status: TaskStatus;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
}) {
    return (
        <div
            className="flex items-center gap-x-2 p-2 hover:bg-persian-50 cursor-pointer"
            onClick={onClick}
        >
            {icon}
            <p className="text-sm text-persian-990">{label}</p>
        </div>
    );
}

function PriorityOption({
    priority,
    onClick,
}: {
    priority: TaskPriority;
    onClick: () => void;
}) {
    const labels: Record<TaskPriority, string> = {
        [TaskPriority.Urgent]: "Urgent",
        [TaskPriority.High]: "High",
        [TaskPriority.Medium]: "Medium",
        [TaskPriority.Low]: "Low",
        [TaskPriority.None]: "None",
    };

    return (
        <div
            className="flex items-center gap-x-2 p-2 hover:bg-persian-50 cursor-pointer"
            onClick={onClick}
        >
            <TaskPriorityIcon priority={priority} />
            <p className="text-sm text-persian-990">{labels[priority]}</p>
        </div>
    );
}

// =============================================================================
// List View Components
// =============================================================================

interface TaskSectionProps {
    title: string;
    tasks: Task[];
    icon: React.ReactNode;
    defaultOpen: boolean;
    dealId: string;
}

function TaskSection({ title, tasks, icon, defaultOpen, dealId }: TaskSectionProps) {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div>
            {/* Section Header */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center gap-x-2 px-4 py-3 hover:bg-gray-50 transition-colors"
            >
                <ChevronDownIcon
                    className={classNames(
                        "w-4 h-4 text-gray-400 transition-transform",
                        isOpen ? "" : "-rotate-90"
                    )}
                />
                {icon}
                <span className="text-sm font-medium text-gray-700">{title}</span>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {tasks.length}
                </span>
            </button>

            {/* Task List */}
            {isOpen && (
                <div className="pb-2">
                    {tasks.length === 0 ? (
                        <p className="px-10 py-3 text-sm text-gray-400">No tasks</p>
                    ) : (
                        tasks
                            .sort((a, b) => a.sortOrder - b.sortOrder)
                            .map((task) => <TaskRow key={task.id} task={task} dealId={dealId} />)
                    )}
                </div>
            )}
        </div>
    );
}

function TaskRow({ task, dealId }: { task: Task; dealId: string }) {
    const { tasks, selectedTasks, setSelectedTasks } = useContext(TasksContext);
    const { handleItemSelection, handleMouseDown } = useShiftSelection(
        tasks,
        selectedTasks,
        setSelectedTasks
    );
    const history = useHistory();

    const isSelected = selectedTasks.includes(task.id);
    const hasSelection = selectedTasks.length > 0;

    const toggleSelection = (e: React.MouseEvent) => {
        handleItemSelection(task.id, e.shiftKey, e);
    };

    const handleRowClick = (e: React.MouseEvent) => {
        // If there's a selection, clicking anywhere toggles this task
        if (hasSelection) {
            toggleSelection(e);
        } else {
            // No selection - navigate to task
            history.push(`/deal/${dealId}/tasks/${task.id}`);
        }
    };

    const handleCheckboxAreaClick = (e: React.MouseEvent) => {
        // Checkbox area always toggles selection, never navigates
        e.stopPropagation();
        toggleSelection(e);
    };

    return (
        <div
            onClick={handleRowClick}
            onMouseDown={handleMouseDown}
            className={classNames(
                "group flex items-center gap-x-3 px-4 py-2 mx-2 mb-1 rounded-lg transition-colors cursor-pointer",
                isSelected ? "bg-persian-100/50" : "hover:bg-gray-50"
            )}
        >
            {/* Checkbox - clicking this area always toggles selection */}
            <div
                onClick={handleCheckboxAreaClick}
                className="p-1 -m-1"
            >
                <Checkbox
                    id={task.id}
                    checked={isSelected}
                    onChange={(e) => {
                        // Handle shift-click for range selection
                        const mouseEvent = e.nativeEvent as MouseEvent;
                        handleItemSelection(task.id, mouseEvent.shiftKey, mouseEvent as unknown as React.MouseEvent);
                    }}
                />
            </div>

            {/* Priority Icon */}
            <TaskPriorityIcon priority={task.priority ?? undefined} />

            {/* Title */}
            <div className="flex-1 min-w-0">
                <p
                    className={classNames(
                        "text-sm truncate",
                        task.status === TaskStatus.Completed
                            ? "text-gray-400 line-through"
                            : "text-gray-800"
                    )}
                >
                    {task.title}
                </p>
                <div className="mt-0.5 flex items-center gap-x-3 text-xs text-concrete-600">
                    <span>
                        Created by {task.createdBy.firstName}{" "}
                        {formatDistanceToNowStrict(fromUnixTime(task.createdAt), {
                            addSuffix: true,
                        })}
                    </span>
                    <SubtaskProgress subTasks={task.subTasks} />
                </div>
            </div>

            {/* Assignees */}
            {task.assignedTo && task.assignedTo.length > 0 && (
                <div className="flex -space-x-1.5">
                    {task.assignedTo.slice(0, 3).map((assignee) => (
                        <Avatar key={assignee.id} account={assignee} size="xs" />
                    ))}
                    {task.assignedTo.length > 3 && (
                        <span className="flex items-center justify-center w-5 h-5 text-xs font-medium bg-gray-200 text-gray-600 rounded-full ring-2 ring-white">
                            +{task.assignedTo.length - 3}
                        </span>
                    )}
                </div>
            )}

            {/* Due Date */}
            <DueDateBadge dueDate={task.dueDate ?? undefined} status={task.status} />
        </div>
    );
}

// =============================================================================
// Kanban View Components
// =============================================================================

interface KanbanViewProps {
    todoTasks: Task[];
    inProgressTasks: Task[];
    completedTasks: Task[];
    dealId: string;
}

function KanbanView({ todoTasks, inProgressTasks, completedTasks, dealId }: KanbanViewProps) {
    const columns = [
        {
            id: TaskStatus.Todo,
            title: "Todo",
            tasks: todoTasks,
            icon: <CircleDashedIcon className="w-4 h-4 text-gray-400" />,
            headerClass: "bg-gray-50",
        },
        {
            id: TaskStatus.InProgress,
            title: "In Progress",
            tasks: inProgressTasks,
            icon: <CircleIcon className="w-4 h-4 text-blue-500" />,
            headerClass: "bg-blue-50",
        },
        {
            id: TaskStatus.Completed,
            title: "Done",
            tasks: completedTasks,
            icon: <CheckCircle2Icon className="w-4 h-4 text-green-500" />,
            headerClass: "bg-green-50",
        },
    ];

    return (
        <div className="flex gap-4 p-4 h-full overflow-x-auto">
            {columns.map((column) => (
                <KanbanColumn
                    key={column.id}
                    id={column.id}
                    title={column.title}
                    tasks={column.tasks}
                    icon={column.icon}
                    headerClass={column.headerClass}
                    dealId={dealId}
                />
            ))}
        </div>
    );
}

interface KanbanColumnProps {
    id: TaskStatus;
    title: string;
    tasks: Task[];
    icon: React.ReactNode;
    headerClass: string;
    dealId: string;
}

function KanbanColumn({ id, title, tasks, icon, headerClass, dealId }: KanbanColumnProps) {
    return (
        <div className="flex-shrink-0 w-80 flex flex-col bg-gray-50/50 rounded-lg">
            {/* Column Header */}
            <div className={classNames("px-3 py-2.5 rounded-t-lg", headerClass)}>
                <div className="flex items-center gap-x-2">
                    {icon}
                    <span className="text-sm font-semibold text-gray-700">{title}</span>
                    <span className="text-xs text-gray-400 bg-white/80 px-2 py-0.5 rounded-full">
                        {tasks.length}
                    </span>
                </div>
            </div>

            {/* Droppable Area */}
            <Droppable droppableId={id}>
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={classNames(
                            "flex-1 p-2 space-y-2 overflow-y-auto min-h-[200px]",
                            snapshot.isDraggingOver ? "bg-gray-100/50" : ""
                        )}
                    >
                        {tasks.length === 0 ? (
                            <p className="text-center py-8 text-sm text-gray-400">No tasks</p>
                        ) : (
                            tasks
                                .sort((a, b) => a.sortOrder - b.sortOrder)
                                .map((task, index) => (
                                    <Draggable key={task.id} draggableId={task.id} index={index}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                <KanbanCard
                                                    task={task}
                                                    dealId={dealId}
                                                    isDragging={snapshot.isDragging}
                                                />
                                            </div>
                                        )}
                                    </Draggable>
                                ))
                        )}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </div>
    );
}

interface KanbanCardProps {
    task: Task;
    dealId: string;
    isDragging: boolean;
}

function KanbanCard({ task, dealId, isDragging }: KanbanCardProps) {
    return (
        <NavLink
            to={`/deal/${dealId}/tasks/${task.id}`}
            className={classNames(
                "block bg-white rounded-lg border p-3 transition-shadow",
                isDragging
                    ? "shadow-lg border-blue-300"
                    : "border-gray-200 hover:shadow-md hover:border-gray-300"
            )}
        >
            {/* Priority & Title */}
            <div className="flex items-start gap-x-2">
                <TaskPriorityIcon priority={task.priority ?? undefined} />
                <p
                    className={classNames(
                        "text-sm font-medium flex-1",
                        task.status === TaskStatus.Completed
                            ? "text-gray-400 line-through"
                            : "text-gray-800"
                    )}
                >
                    {task.title}
                </p>
            </div>

            {/* Meta Info */}
            <div className="mt-3 flex items-center justify-between">
                {/* Assignees */}
                <div className="flex -space-x-1.5">
                    {task.assignedTo && task.assignedTo.length > 0 ? (
                        <>
                            {task.assignedTo.slice(0, 3).map((assignee) => (
                                <Avatar key={assignee.id} account={assignee} size="xs" />
                            ))}
                            {task.assignedTo.length > 3 && (
                                <span className="flex items-center justify-center w-5 h-5 text-xs font-medium bg-gray-200 text-gray-600 rounded-full ring-2 ring-white">
                                    +{task.assignedTo.length - 3}
                                </span>
                            )}
                        </>
                    ) : (
                        <span className="text-xs text-gray-400">Unassigned</span>
                    )}
                </div>

                <div className="flex items-center gap-x-2">
                    <SubtaskProgress subTasks={task.subTasks} />
                    <DueDateBadge dueDate={task.dueDate ?? undefined} status={task.status} />
                </div>
            </div>
        </NavLink>
    );
}

// =============================================================================
// Shared Components
// =============================================================================

function DueDateBadge({
    dueDate,
    status,
}: {
    dueDate?: number;
    status: TaskStatus;
}) {
    if (!dueDate) {
        return null;
    }

    const date = fromUnixTime(dueDate);
    const isOverdue = isBefore(date, new Date()) && status !== TaskStatus.Completed;

    return (
        <div
            className={classNames(
                "flex items-center gap-x-1 px-2 py-1 rounded text-xs",
                isOverdue
                    ? "bg-red-50 text-red-600"
                    : status === TaskStatus.Completed
                        ? "bg-gray-50 text-gray-400"
                        : "bg-gray-50 text-gray-500"
            )}
        >
            <CalendarIcon className="w-3 h-3" />
            <span>{format(date, "MMM d")}</span>
        </div>
    );
}

function SubtaskProgress({
    subTasks,
}: {
    subTasks?: Array<{ status: TaskStatus }>;
}) {
    if (!subTasks || subTasks.length === 0) {
        return null;
    }

    const completed = subTasks.filter((task) => task.status === TaskStatus.Completed).length;
    const total = subTasks.length;
    const ratio = total === 0 ? 0 : completed / total;
    const radius = 5;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - ratio * circumference;

    return (
        <div className="flex items-center gap-x-1 text-xs text-gray-500">
            <svg width="12" height="12" className="text-gray-300">
                <circle
                    cx="6"
                    cy="6"
                    r={radius}
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                />
                <circle
                    cx="6"
                    cy="6"
                    r={radius}
                    stroke="#4f46e5"
                    strokeWidth="2"
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    transform="rotate(-90 6 6)"
                />
            </svg>
            <span>
                {completed}/{total}
            </span>
        </div>
    );
}
